@if (reviewerSession && hasFindings()) {
<div class="reviewers-details">
  <div>
    <button mat-button (click)="showView()">&lt; All Reviewers</button>
  </div>
  <div class="reviewers-details__header">
    <div class="reviewers-details__header-content">
      <div>
        <h3 class="reviewers-details__title">{{ reviewerSession.document }}</h3>
        <p class="reviewers-details__meta">
          Reviewed on {{ reviewerSession.created_at | date: 'yyyy-MM-dd' }}
        </p>
      </div>
      <button 
        mat-button 
        class="export-button" 
        (click)="exportToExcel()"
        (keydown.enter)="exportToExcel()"
        (keydown.space)="exportToExcel()"
        [disabled]="isExporting"
        [attr.aria-label]="isExporting ? 'Exporting review findings to Excel, please wait' : 'Export review findings to Excel'"
        [attr.aria-busy]="isExporting"
        [attr.aria-disabled]="isExporting"
        type="button"
        role="button"
        tabindex="0">
        @if (isExporting) {
          <mat-spinner diameter="20" class="export-button__spinner" aria-hidden="true"></mat-spinner>
          <span aria-live="polite">Exporting...</span>
        } @else {
          <ng-container>
            <mat-icon aria-hidden="true">file_download</mat-icon>
            <span>Export</span>
          </ng-container>
        }
      </button>
    </div>
  </div>

  <!-- Overview section with suggestion counts -->
  <div class="overview u-margin-left u-margin-bottom">
    <h3 class="overview__title">Review Progress</h3>
    <div class="overview__stats">
      <div class="overview__stat-item">
        <span class="overview__count overview__count--unresolved">{{ getUnresolvedCount() }}</span>
        <span class="overview__label">Unresolved</span>
      </div>
      <div class="overview__stat-item">
        <span class="overview__count overview__count--resolved">{{ getResolvedCount() }}</span>
        <span class="overview__label">Resolved</span>
      </div>
      <div class="overview__stat-item">
        <span class="overview__count overview__count--total">{{ getTotalCount() }}</span>
        <span class="overview__label">Total</span>
      </div>
      <div class="overview__progress-container">
        <div class="overview__progress-bar" [style.width.%]="getResolvedCount() / getTotalCount() * 100"></div>
      </div>
    </div>
  </div>

  <!-- Duplicate filter toggle -->
  <div class="duplicate-toggle u-margin-left u-margin-bottom">
    <mat-slide-toggle 
      class="duplicate-toggle__control"
      [(ngModel)]="hideDuplicates"
      color="primary">
      <span class="duplicate-toggle__label">Hide Possible Duplicates</span>
    </mat-slide-toggle>
  </div>




  <mat-accordion>
    <!-- Findings structure for multi-persona reviews -->
    @for (finding of getFilteredFindings(); track finding.insight_id; let findingIdx = $index) {
    @if (!finding.resolved) {
    <mat-expansion-panel class="panel--unresolved" [attr.data-test-id]="'reviewers-unresolved-finding-' + findingIdx">
      <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
        <div class="panel__header-content layout layout--column">
          <span class="panel__theme">
            <span 
              class="severity-badge" 
              [ngClass]="getSeverityClass(finding.severity)"
              [attr.aria-label]="getSeverityLabel(finding.severity)"
              role="status">
              {{ finding.severity }}
            </span>
            <span class="persona-tag">{{ finding.persona.name }}</span>
            <span *ngIf="isDuplicate(finding)" class="duplicate-badge">{{ finding.duplicate | titlecase }}</span>
          </span>
          <span class="location-path">{{ getLocationPath(finding) }}@if (finding.finding) { | {{ finding.finding }} }</span>
        </div>
      </mat-expansion-panel-header>
      <div class="insight-content u-margin-bottom">
        <!-- Deviation Section -->
        @if (finding.deviation) {
        <div class="deviation">
          <h3 class="deviation__title">Finding</h3>
          <div class="deviation__container">
            <p class="deviation__content">{{ finding.deviation }}</p>
          </div>
        </div>
        }

        <!-- Document Text Section -->
        <div class="document-text">
          <h3 class="document-text__title">Document Text</h3>
          <div class="document-text__container">
            <p class="document-text__content">{{ finding.original_text }}</p>
          </div>
        </div>

        <!-- Recommendation Section -->
        <div class="recommendation">
          <h3 class="recommendation__title">Recommendation</h3>
          <div class="recommendation__container">
            <p class="recommendation__content">{{ finding.recommendation }}</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="recommendation__actions">
          <button mat-button class="action-button" (click)="toClipboard(finding.recommendation)">
            <mat-icon>content_copy</mat-icon>
            Copy
          </button>
          <button mat-button class="action-button" (click)="toggleFinding(finding.insight_id)">
            <mat-icon>check</mat-icon>
            Resolve
          </button>
        </div>
      </div>
    </mat-expansion-panel>
    }
    }
  </mat-accordion>
  <div class="layout__bottom">
    <h3 class="u-margin-left u-padding-top-lg">Resolved Issues</h3>
    <mat-accordion>
      @for (finding of getFilteredFindings(); track finding.insight_id; let findingIdx = $index) {
      @if (finding.resolved) {
      <mat-expansion-panel class="panel--resolved" [attr.data-test-id]="'reviewers-resolved-finding-' + findingIdx">
        <mat-expansion-panel-header collapsedHeight="auto" expandedHeight="auto">
          <div class="panel__header-content layout layout--column">
            <span class="panel__theme">
              <span 
                class="severity-badge" 
                [ngClass]="getSeverityClass(finding.severity)"
                [attr.aria-label]="getSeverityLabel(finding.severity)"
                role="status">
                {{ finding.severity }}
              </span>
              <span class="persona-tag">{{ finding.persona.name }}</span>
              <span *ngIf="isDuplicate(finding)" class="duplicate-badge">{{ finding.duplicate | titlecase }}</span>
            </span>
            <span class="location-path">{{ getLocationPath(finding) }}@if (finding.finding) { | {{ finding.finding }} }</span>
          </div>
        </mat-expansion-panel-header>
        <div class="insight-content u-margin-bottom">
          <!-- Deviation Section -->
          @if (finding.deviation) {
          <div class="deviation">
            <h3 class="deviation__title">Finding</h3>
            <div class="deviation__container">
              <p class="deviation__content">{{ finding.deviation }}</p>
            </div>
          </div>
          }

          <!-- Document Text Section -->
          <div class="document-text">
            <h3 class="document-text__title">Document Text</h3>
            <div class="document-text__container">
              <p class="document-text__content">{{ finding.original_text }}</p>
            </div>
          </div>

          <!-- Recommendation Section -->
          <div class="recommendation">
            <h3 class="recommendation__title">Recommendation</h3>
            <div class="recommendation__container">
              <p class="recommendation__content">{{ finding.recommendation }}</p>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="recommendation__actions">
            <button mat-button class="action-button" (click)="toClipboard(finding.recommendation)">
              <mat-icon>content_copy</mat-icon>
              Copy
            </button>
            <button mat-button class="action-button" (click)="toggleFinding(finding.insight_id)">
              <mat-icon>undo</mat-icon>
              Unresolve
            </button>
          </div>
        </div>
      </mat-expansion-panel>
      }
      }
    </mat-accordion>
  </div>
</div>
}